import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { selectAuthRoles, State } from 'src/app/store/auth/auth.reducers';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import { offlineHomeFor } from 'src/app/core/offline/offline-routes';
import { isAllowedByScopes, scopesFor } from './role-scope';

/**
 * Confines a user holding a confined role (ROLE_SCOPES: `trainee`, `intelligence-viewer`) to the
 * routes its scope allows, redirecting anything else to the scope's `home`. Unconfined users are
 * unaffected. Wired as `canActivateChild` on the top-level `app` route so it runs for every
 * `/app/**` descendant (including lazy modules and `set-insert-out`), which also covers the
 * environment's `homePage` (`/app/shop`): a confined user sent there after login, after a 403 or by
 * CheckAuthService lands on its own home instead.
 *
 * It replaces TraineeAllowlistGuard, whose allow-list it carries over unchanged.
 *
 * OFFLINE MODE has no session, so the store's roles are empty: the remembered identity's roles are
 * confined instead (CheckAuthService already refuses what they do not allow; this keeps the guard
 * honest on its own), and a refusal lands on the offline home, never on a page offline cannot serve.
 */
@Injectable({ providedIn: 'root' })
export class RoleScopeGuard implements CanActivateChild {
  constructor(private store: Store<State>, private router: Router, private offlineMode: OfflineModeState) {}

  canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.store.pipe(
      select(selectAuthRoles),
      take(1),
      map(roles => {
        const identity = this.offlineMode.active && !roles?.length ? this.offlineMode.identity : null;
        const scopes = scopesFor(identity ? identity.roles : roles);
        if (isAllowedByScopes(scopes, state.url)) {
          return true;
        }
        return this.router.parseUrl(identity ? offlineHomeFor(identity.roles) : scopes[0].home);
      })
    );
  }
}
